<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <div>
        <v-toolbar class="elevation-1">
            <v-toolbar-title>{{ title }}</v-toolbar-title>

            <div class="flex-grow-1"></div>

            <template v-if="$vuetify.breakpoint.smAndUp">
                <v-btn color="primary" class="ma-0" min-width="0" @click="openAddDialog">
                    <v-icon>add</v-icon>
                </v-btn>
            </template>
        </v-toolbar>

        <!-- Data table -->
        <div class="container" v-if="dTableHeaders.length > 0">
            <ResponsiveDataTable :loading="dTableLoading"
                                 :headers="dTableHeaders"
                                 :items="dTableData"
                                 :enableSearch="false"
                                 :totalItems="dTableTotalItems"
                                 :selectEnabled="true"
                                 @options-changed="optionsChanged"
            >
                <template v-slot:color_pill="slotProps">
                    <v-chip :color="slotProps.item[slotProps.col_name]" pill></v-chip>
                </template>
                <template v-slot:actions="slotProps">
                    <v-icon class="material-icons edit_icon"
                            color="primary"
                            @click="openEditDialog(slotProps.item)">
                        edit
                    </v-icon>
                    <v-icon v-if="slotProps.item.role !== 'admin'"
                            color="error"
                            class="material-icons delete_icon"
                            @click="deleteItem(slotProps.item)"
                    > delete
                    </v-icon>
                </template>
                <template v-slot:state_toggle="slotProps">
                    <v-switch v-if="slotProps.item.role !== 'admin'"
                              v-model="slotProps.item.is_active"
                              @click.stop="changeState(slotProps.item)"
                              class="state_switch"
                    ></v-switch>
                </template>
                <template v-slot:avatar="slotProps">
                    <avatar :username="slotProps.item" :size="36"></avatar>
                </template>
            </ResponsiveDataTable>
        </div>

        <!--Form dialog-->
        <FormDialog :dialog="formDialog"
                    :title="formDialogTitle"
                    @submit="submit"
                    @close="closeFormDialog"
        >
            <template v-slot:formFields>
                <!--suppress JSUnresolvedVariable -->
                <v-col :cols="grid.cols" :sm="grid.sm" :md="grid.md" :lg="grid.lg"
                       v-for="(field, index) in formFields" :key="index"
                >
                    <!-- Dynamically create inputs based on input type (e.g. select, color, e.t.c.) -->
                    <!-- Select input: -->
                    <v-select
                            v-if="(inactiveFields.indexOf(field.name) === -1 || current_item_id === null) && (field.type === 'select' || field.type === 'select.multiple')"
                            class="custom_textfield"
                            :name="field.name"
                            :label="field.label"
                            type="select"
                            :multiple="isMultipleSelect(field.type)"
                            v-model="form[field.name]"
                            :items="selectFieldItems[field.name]"
                            item-text="name"
                            item-value="id"
                            :error-messages="fieldErrors(field.name)"
                            @input="checkValid(field.name)"
                            @blur="checkValid(field.name)"
                            outlined
                    ></v-select>
                    <!-- Color input: -->
                    <swatches
                            v-else-if="(inactiveFields.indexOf(field.name) === -1 || current_item_id === null) && field.type === 'color'"
                            v-model="form[field.name]"
                            colors="text-advanced"
                    >
                        <v-text-field
                                class="custom_textfield "
                                v-model="form[field.name]"
                                :name="field.name"
                                :label="field.label"
                                :clearable="false"
                                :error-messages="fieldErrors(field.name)"
                                @input="checkValid(field.name)"
                                @blur="checkValid(field.name)"
                                outlined
                                slot="trigger"
                                readonly
                        ></v-text-field>

                    </swatches>
                    <!-- Text input: -->
                    <v-text-field v-else-if="(inactiveFields.indexOf(field.name) === -1 || current_item_id === null)"
                                  class="custom_textfield"
                                  v-model="form[field.name]"
                                  :name="field.name"
                                  :label="field.label"
                                  :clearable="true"
                                  :type="field.type"
                                  :error-messages="fieldErrors(field.name)"
                                  @input="checkValid(field.name)"
                                  @blur="checkValid(field.name)"
                                  outlined
                    ></v-text-field>
                </v-col>
            </template>
        </FormDialog>

        <!-- Confirm action dialog -->
        <ConfirmActionDialog :dialog="confirmDialog"
                             :header="confirmDialogHeader"
                             :message="confirmDialogMessage"
                             :color="confirmDialogColor"
                             :styleClass="confirmDialogClass"
                             :action="confirm_action"
                             @close="closeConfirmDialog"
                             @delete="deleteItemConfirmed"
                             @switch="stateChangeConfirmed"
        ></ConfirmActionDialog>
    </div>
</template>

<script>
    import ResponsiveDataTable from '../components/ResponsiveDataTable.vue'
    import FormDialog from '../components/FormDialog.vue'
    import ConfirmActionDialog from '../components/ConfirmActionDialog.vue'
    import {validationMixin} from 'vuelidate'
    import {
        required, requiredIf, minLength, maxLength, email,
        alphaNum, alpha, numeric, integer, sameAs
    } from 'vuelidate/lib/validators'
    import Swatches from 'vue-swatches'
    import "vue-swatches/dist/vue-swatches.min.css"
    import Avatar from 'vue-avatar'

    export default {
        components: {
            ResponsiveDataTable,
            FormDialog,
            ConfirmActionDialog,
            Swatches,
            Avatar
        },
        mixins: [validationMixin],
        validations() {
            return {
                form: this.validationRules
            }
        },
        props: {
            title: {type: String}
        },
        computed: {
            formClientId() {
                if (this.form) {
                    return this.form.client_id;
                }
            },
            settingName() {
                const stringParts = this.$route.name.toString().split("_");
                return stringParts[1];
            },
        },
        data() {
            return {
                // General
                current_item_id: null,
                validationRules: {},
                newEntryTitle: null,

                // Confirmation dialog
                confirm_action: null,
                confirmDialog: false,
                confirmDialogColor: null,
                confirmDialogHeader: null,
                confirmDialogMessage: null,
                confirmDialogClass: null,

                // Form dialog
                grid: [],
                formDialogTitle: null,
                formDialog: false,
                form: null,
                formFields: [],
                inactiveFields: [],
                selectFieldItems: [],

                // Data table
                getTableDataEndpoint: null,
                dTableHeaders: [],
                dTableData: [],
                dTableTotalItems: null,
                dTableOptions: {
                    page: null,
                    itemsPerPage: null,
                    sortBy: [],
                    sortDesc: [],
                    groupBy: [],
                    groupDesc: [],
                    multiSort: false,
                    mustSort: false
                },
                dTableLoading: false,
            }
        },
        beforeRouteEnter(to, from, next) {
            // Current setting
            const settingName = to.path;

            next(async vm => {
                vm.dTableLoading = true;
                switch (settingName) {
                    case '/settings/clients':
                        // Data and dataTable setup:
                        // Set endpoint for data of current setting
                        vm.getTableDataEndpoint = '/api/clients';
                        // Set table headers
                        vm.dTableHeaders = [
                            {
                                text: `${vm.$t('general.name')}`,
                                value: 'name',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('general.email')}`,
                                value: 'email',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('settings.color')}`,
                                value: 'color',
                                sortable: false,
                            },
                            {
                                text: `${vm.$t('settings.hourlyRate')}`,
                                value: 'hourly_rate',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('general.actions')}`,
                                align: 'right',
                                class: 'actions_header',
                                value: 'actions',
                                sortable: false,
                            }
                        ];
                        // Load doys for select form field
                        vm.selectFieldItems['doy'] = (await vm.$store.dispatch('fetchData', {url: '/api/doys'}))[0];

                        // Form setup:
                        // Set Form data
                        vm.form = {
                            name: null,
                            email: null,
                            job_description: null,
                            street: null,
                            street_no: null,
                            city: null,
                            vat: null,
                            doy: null,
                            hourly_rate: null,
                            color: '#ffffff'
                        };
                        // Set new entry form fields data
                        vm.formFields = [
                            {
                                name: 'name',
                                label: `${vm.$t('general.name')}`,
                                type: 'text',
                            },
                            {
                                name: 'email',
                                label: `${vm.$t('general.email')}`,
                                type: 'text',
                            },
                            {
                                name: 'job_description',
                                label: `${vm.$t('forms.job_description')}`,
                                type: 'text',
                            },
                            {
                                name: 'street',
                                label: `${vm.$t('forms.street')}`,
                                type: 'text',
                            },
                            {
                                name: 'street_no',
                                label: `${vm.$t('forms.street_no')}`,
                                type: 'text',
                            },
                            {
                                name: 'city',
                                label: `${vm.$t('forms.city')}`,
                                type: 'text',
                            },
                            {
                                name: 'vat',
                                label: `${vm.$t('forms.vat')}`,
                                type: 'text',
                            },
                            {
                                name: 'doy',
                                label: `${vm.$t('forms.doy')}`,
                                type: 'select',
                            },
                            {
                                name: 'hourly_rate',
                                label: `${vm.$t('settings.hourlyRate')}`,
                                type: 'text',
                            },
                            {
                                name: 'color',
                                label: `${vm.$t('forms.chooseColor')}`,
                                type: 'color',
                            },
                        ];
                        // Fields that might not show
                        vm.inactiveFields = [];
                        // Set form validation
                        vm.validationRules = {
                            name: {
                                required,
                            },
                            email: {
                                required,
                                email
                            },
                            job_description: {},
                            hourly_rate: {
                                numeric,
                                required,
                            },
                        };
                        // Set form grid layout
                        vm.grid = {
                            lg: 4, md: 6, sm: 12, cols: 12
                        };
                        break;
                    case '/settings/projects':
                        // Data and dataTable setup:
                        // Set endpoint for data of current setting
                        vm.getTableDataEndpoint = '/api/projects';
                        // Set table headers
                        vm.dTableHeaders = [
                            {
                                text: `${vm.$t('general.name')}`,
                                value: 'name',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('settings.clientName')}`,
                                value: 'client',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('settings.backgroundColor')}`,
                                value: 'background_color',
                                sortable: false,
                            },
                            {
                                text: `${vm.$t('settings.hourlyRate')}`,
                                value: 'hourly_rate',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('settings.state')}`,
                                value: 'state',
                                sortable: false,
                            },
                            {
                                text: `${vm.$t('general.actions')}`,
                                value: 'actions',
                                align: 'right',
                                class: 'actions_header',
                                sortable: false,
                            }
                        ];
                        // Load clients for select form field
                        vm.selectFieldItems['client_id'] = (await vm.$store.dispatch('fetchData', {url: '/api/clients/all'}))[0];
                        // Load active users with developer role
                        vm.selectFieldItems['linked_users'] = (await vm.$store.dispatch('fetchData', {
                            url: '/api/users/devs',
                            params: {conditions: {is_active: 1}}
                        }))[0];

                        // Form setup:
                        // Set Form data
                        vm.form = {
                            client_id: null,
                            name: null,
                            background_color: '#ffffff',
                            hourly_rate: null,
                            text_color: '#ffffff',
                            is_active: null,
                            linked_users: []
                        };
                        // Set new entry form fields data
                        vm.formFields = [
                            {
                                name: 'client_id',
                                label: `${vm.$t('settings.clientName')}`,
                                type: 'select',
                            },
                            {
                                name: 'name',
                                label: `${vm.$t('general.name')}`,
                                type: 'text',
                            },
                            {
                                name: 'background_color',
                                label: `${vm.$t('forms.backgroundColor')}`,
                                type: 'color',
                            },
                            {
                                name: 'hourly_rate',
                                label: `${vm.$t('settings.hourlyRate')}`,
                                type: 'text',
                            },
                            {
                                name: 'text_color',
                                label: `${vm.$t('forms.text_color')}`,
                                type: 'color',
                            },
                            {
                                name: 'linked_users',
                                label: `${vm.$t('forms.linked_users')}`,
                                type: 'select.multiple'
                            }

                        ];
                        // Fields that might not show
                        vm.inactiveFields = [];
                        // Set form validation
                        vm.validationRules = {
                            client_id: {
                                required,
                            },
                            name: {
                                required,
                            },
                            hourly_rate: {
                                integer
                            },
                        };
                        // Set form grid layout
                        vm.grid = {
                            lg: 6, md: 6, sm: 12, cols: 12
                        };
                        break;
                    case '/settings/users':
                        // Data and dataTable setup:
                        // Set endpoint for data of current setting
                        vm.getTableDataEndpoint = '/api/users';
                        // set table headers
                        vm.dTableHeaders = [
                            {
                                text: `${vm.$t('settings.photo')}`,
                                value: 'avatar',
                                sortable: false,
                            },
                            {
                                text: `${vm.$t('general.name')}`,
                                value: 'name',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('general.email')}`,
                                value: 'email',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('general.role')}`,
                                value: 'role',
                                sortable: true
                            },
                            {
                                text: `${vm.$t('settings.color')}`,
                                value: 'color',
                                sortable: false,
                            },
                            {
                                text: `${vm.$t('settings.state')}`,
                                value: 'state',
                                sortable: false,
                            },
                            {
                                text: `${vm.$t('general.actions')}`,
                                value: 'actions',
                                align: 'right',
                                class: 'actions_header',
                                sortable: false,
                            }
                        ];

                        // Form setup:
                        // Set Form data
                        vm.form = {
                            name: null,
                            email: null,
                            password: null,
                            password_confirmation: null,
                            color: '#ffffff',
                            is_active: null
                        };
                        // Set new entry form fields data
                        vm.formFields = [
                            {
                                name: 'name',
                                label: `${vm.$t('general.name')}`,
                                type: 'text',
                            },
                            {
                                name: 'email',
                                label: `${vm.$t('general.email')}`,
                                type: 'text',
                            },
                            {
                                name: 'password',
                                label: `${vm.$t('general.password')}`,
                                type: 'password',
                            },
                            {
                                name: 'password_confirmation',
                                label: `${vm.$t('general.confirmPassword')}`,
                                type: 'password',
                            },
                            {
                                name: 'color',
                                label: `${vm.$t('forms.chooseColor')}`,
                                type: 'color',
                            },
                        ];
                        // Fields that might not show in certain cases
                        vm.inactiveFields = [
                            'password',
                            'password_confirmation'
                        ];
                        // Set form validation
                        vm.validationRules = {
                            name: {
                                required
                            },
                            email: {
                                required,
                                email
                            },
                            password: {
                                required: requiredIf(() => {
                                    return vm.id === null;
                                }),
                                minLength: minLength(6)
                            },
                            password_confirmation: {
                                requiredIf: requiredIf(() => {
                                    return vm.id === null;
                                }),
                                minLength: minLength(6),
                                sameAs: sameAs('password')
                            },
                        };
                        // Set form grid layout
                        vm.grid = {
                            lg: 6, md: 6, sm: 12, cols: 12
                        };
                        break;
                    case '/settings/bank-accounts':
                        // Data and dataTable setup:
                        // Set endpoint for data of current setting
                        vm.getTableDataEndpoint = '/api/bank-accounts';
                        // set table headers
                        vm.dTableHeaders = [
                            {
                                text: `${vm.$t('general.name')}`,
                                value: 'name',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('general.swift')}`,
                                value: 'swift',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('general.address')}`,
                                value: 'address',
                                sortable: true
                            },
                            {
                                text: `${vm.$t('general.iban')}`,
                                value: 'iban',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('general.company')}`,
                                value: 'company_name',
                                sortable: true,
                            },
                            {
                                text: `${vm.$t('settings.state')}`,
                                value: 'state',
                                sortable: false,
                            },
                            {
                                text: `${vm.$t('general.actions')}`,
                                value: 'actions',
                                align: 'right',
                                class: 'actions_header',
                                sortable: false,
                            }
                        ];
                        // Load companies for select form field
                        vm.selectFieldItems['company_id'] = (await vm.$store.dispatch('fetchData', {url: '/api/companies'}))[0];

                        // Form setup:
                        // Set Form data
                        vm.form = {
                            name: null,
                            swift: null,
                            address: null,
                            iban: null,
                            company_id: null,
                            is_active: 1
                        };
                        // Set new entry form fields data
                        vm.formFields = [
                            {
                                name: 'name',
                                label: `${vm.$t('general.name')}`,
                                type: 'text',
                            },
                            {
                                name: 'swift',
                                label: `${vm.$t('general.swift')}`,
                                type: 'text',
                            },
                            {
                                name: 'address',
                                label: `${vm.$t('general.address')}`,
                                type: 'text',
                            },
                            {
                                name: 'iban',
                                label: `${vm.$t('general.iban')}`,
                                type: 'text',
                            },
                            {
                                name: 'company_id',
                                label: `${vm.$t('general.company')}`,
                                type: 'select',
                            }
                        ];
                        // Fields that might not show in certain cases
                        vm.inactiveFields = [];
                        // Set form validation
                        vm.validationRules = {
                            name: {
                                required
                            },
                            swift: {
                                required
                            },
                            address: {
                                required
                            },
                            iban: {
                                required
                            },
                            company_id: {
                                required
                            }
                        };
                        // Set form grid layout
                        vm.grid = {
                            lg: 6, md: 6, sm: 12, cols: 12
                        };
                        break;
                    default:
                        break;
                }
                // When changing settings url and not create setting sfc instance again get manually data from API
                if (from.meta.group === to.meta.group) {
                    vm.dTableData = [];
                    vm.fetchTableData();
                }
                // If form dialog happens to be opened, close it
                vm.formDialog = false;
            });
        },
        methods: {
            // Checks if data table options were changed.
            optionsChanged(options) {
                let options_changed = false;
                this.dTableLoading = true;

                // Check if options changed
                for (let prop in this.dTableOptions) {
                    if (this.dTableOptions[prop] !== options[prop]) {
                        options_changed = true;
                        break;
                    }
                }

                if (options_changed) {
                    this.dTableOptions = Object.assign({}, options);
                    this.fetchTableData();
                }
            },
            // Request API for data table data.
            async fetchTableData() {
                this.dTableData = [];
                let response = await this.$store.dispatch('fetchData', {
                    url: `${this.getTableDataEndpoint}`,
                    params: {options: this.dTableOptions}
                });

                this.dTableData = response.data;
                this.dTableTotalItems = response.total;
                this.dTableLoading = false;
            },

            /**
             * Datatable actions
             */
            // Handles edit button event, by opening form dialog to edit item.
            openEditDialog(item) {
                this.setFormData(item);

                this.formDialogTitle = this.$t(`forms.edit_${this.settingName}_title`);
                this.formDialog = true;
            },

            // Handles delete button event, by opening a confirmation dialog that does the rest (or not..).
            deleteItem(item) {
                this.confirmDialog = true;
                this.confirmDialogClass = 'fail';
                this.confirmDialogHeader = this.$t(`confirmation.${this.settingName}_deletion`);
                this.confirmDialogMessage = this.$t(`confirmation.${this.settingName}_deletion_text`);
                this.confirm_action = 'delete';
                this.current_item_id = item.id;
            },
            // Executes delete request for specified item
            async deleteItemConfirmed() {
                let response = await this.$store.dispatch('sendData', {
                    method: 'delete',
                    url: `${this.getTableDataEndpoint}/${this.current_item_id}`,
                });

                if (response.status === 200) {
                    // Find index of item in table and remove item
                    let deletedItemIndex = this.dTableData.findIndex((item) => {
                        return item.id === this.current_item_id;
                    });
                    this.dTableData.splice(deletedItemIndex, 1);
                    this.fetchTableData();
                }

                this.closeConfirmDialog();
            },

            // Change state toggle.
            changeState(item) {
                // If item is active display deactivation dialog
                if (item.is_active === 1) {
                    // Deactivate
                    this.confirmDialogClass = 'fail';
                    this.confirmDialogHeader = this.$t(`confirmation.${this.settingName}_deactivation`);
                    this.confirmDialogMessage = this.$t(`confirmation.${this.settingName}_deactivation_text`);
                    // Else, display activation dialog
                } else {
                    // Activate
                    this.confirmDialogClass = 'success';
                    this.confirmDialogHeader = this.$t(`confirmation.${this.settingName}_activation`);
                    this.confirmDialogMessage = this.$t(`confirmation.${this.settingName}_activation_text`);
                }

                this.confirm_action = 'switch';
                this.confirmDialog = true;

                // Set form and id to current item
                this.current_item_id = item.id;
                this.setFormData(item);
            },
            // Executes patch request to update item when state change action is confirmed.
            async stateChangeConfirmed() {
                // Set is_active new value to form in order to pass it to the request and update db.
                this.form.is_active = this.form.is_active === 1 ? 0 : 1;

                // Send patch request to save changes.
                let response = await this.$store.dispatch('sendData', {
                    method: 'patch',
                    url: `${this.getTableDataEndpoint}/${this.current_item_id}`,
                    data: this.form
                });

                if (response.status === 200) {
                    // Find table row that contains the item
                    const row = this.dTableData.find((row) => row.id === this.current_item_id);

                    // Set switch input
                    row.is_active = row.is_active === 1 ? 0 : 1;
                }

                this.closeConfirmDialog();
            },

            // Close action confirm dialog
            closeConfirmDialog() {
                this.confirmDialog = false;
                this.resetDialogData();
            },

            /**
             * Form actions
             */
            // I field fails validation, returns the errors.
            fieldErrors(fieldName) {
                if (this.$v.form[fieldName]) {
                    const errors = [];

                    // if there are no validation errors, return empty array.
                    if (!this.$v.form[fieldName].$dirty) return errors;

                    // gather validation rules of current field
                    const fieldValidationRules = this.validationRules[fieldName];

                    for (let rule in fieldValidationRules) {
                        // check whether field actually has current rule and push rule to array
                        !this.$v.form[fieldName][rule] && errors.push(this.$t(`validation.${rule}`));
                    }
                    return errors;
                }
            },
            // Form dialog
            openAddDialog() {
                this.current_item_id = null;
                this.formDialogTitle = this.$t(`forms.new_${this.settingName}_title`);
                this.formDialog = true;
            },
            // Submits the data to the db and refreshes the table when adding or editing an item
            async submit() {
                // Check if form input is valid.
                this.$v.form.$touch();
                if (!this.$v.form.$invalid) {
                    let promise = new Promise(async (resolve, reject) => {
                        // If id is not null, then perform patch request to update item
                        if (this.current_item_id) {
                            let response = await this.$store.dispatch('sendData', {
                                method: 'patch',
                                url: `${this.getTableDataEndpoint}/${this.current_item_id}`,
                                data: this.form
                            });
                            resolve(response)
                        } else {
                            // Perform post request to add new form entry
                            let response = await this.$store.dispatch('sendData', {
                                method: 'post',
                                url: `${this.getTableDataEndpoint}`,
                                data: this.form
                            });
                            resolve(response)
                        }
                    });

                    promise.then((response) => {
                        if (response.status === 200) {
                            this.fetchTableData();
                            this.closeFormDialog();
                        }
                    })
                }
            },
            // Close dialog form and reset form
            closeFormDialog() {
                // Reset validator.
                this.$v.$reset();
                this.formDialog = false;

                this.resetDialogData();
            },
            // Reset the form and dialogs to initial state
            resetDialogData() {
                this.current_item_id = null;

                // Reset form
                for (let key in this.form) {
                    if (this.form.hasOwnProperty(key)) {
                        this.form[key] = (['color', 'text_color', 'background_color'].indexOf(key) > -1) ? '#ffffff' : null;
                    }
                }
            },
            // If given field name corresponds to an actual field, run validations
            checkValid(field_name) {
                if (this.$v.form[field_name]) {
                    this.$v.form[field_name].$touch();
                }
            },
            // Sets form data to current item being edited and handles special
            // case in which project item is being edited.
            setFormData(item) {
                this.current_item_id = item.id;

                // If we are in projects setting there will be a client_id prop in form.
                // First, get current client's id to trigger client data updates
                if (this.form.hasOwnProperty('client_id')) {
                    this.form.client_id = item.client_id;
                }
                // Wait until next DOM cycle before updating the rest of the fields so that
                // the client data have already been set (and client_id has already been set).
                this.$nextTick().then(() => {
                    for (let val in this.form) {
                        if (val !== 'client_id') {
                            this.form[val] = item[val];
                        }
                    }
                });
            },

            isMultipleSelect(type) {
                return (type === 'select.multiple');
            }
        },
        watch: {
            formClientId(client_id) {
                if (client_id) {
                    let client = this.selectFieldItems.find((client) => client.id === this.form.client_id);

                    if (client) {
                        this.form.hourly_rate = client.hourly_rate;
                        this.form.background_color = client.color;
                    }
                }
            }
        }
    };
</script>